import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import "../css/videoPlayer.css";

const explainerSlides = [
  {
    eyebrow: "Start here",
    title: "Welcome customers into Cling",
    copy:
      "A clean onboarding experience introduces Cling as a local platform for members and businesses before the user even joins their first merchant program.",
    narration:
      "Welcome to Cling, where local discovery, loyalty, and digital storefronts come together in one app.",
    image: require("../assets/explainer/welcome.png"),
    alt: "Cling welcome screen",
    tone: "customer",
  },
  {
    eyebrow: "Customer signup",
    title: "Registration starts with a simple mobile-first flow",
    copy:
      "Customers can create an account quickly and move into discovery, loyalty, and local engagement without a heavy setup experience.",
    narration:
      "Customers can sign up quickly with a simple mobile-first flow and begin their Cling journey in just a few steps.",
    image: require("../assets/explainer/register.png"),
    alt: "Cling registration screen",
    tone: "customer",
  },
  {
    eyebrow: "Private profile",
    title: "After signup, the user lands in their private Cling profile",
    copy:
      "The private profile is the home base for identity, businesses, rewards, posts, and the next actions that shape the user journey inside the app.",
    narration:
      "After creating the account, the user lands in their private Cling profile, which becomes the starting point for both merchant and customer actions.",
    image: require("../assets/explainer/private-profile.png"),
    alt: "Cling private profile screen",
    tone: "customer",
    overlays: [
      {
        className: "cling-explainer__privacy-avatar cling-explainer__privacy-avatar--profile",
        label: "C",
      },
      {
        className: "cling-explainer__privacy-mask cling-explainer__privacy-mask--profile-name",
      },
      {
        className: "cling-explainer__privacy-mask cling-explainer__privacy-mask--profile-email",
      },
      {
        className: "cling-explainer__privacy-mask cling-explainer__privacy-mask--profile-location",
      },
    ],
  },
  {
    eyebrow: "Merchant path",
    title: "From profile, tap Add Business to start the merchant journey",
    copy:
      "Cling lets a user move naturally from a personal profile into merchant mode by adding and managing one or more businesses from the same account.",
    narration:
      "From the private profile, the user can tap Add Business and begin setting up a storefront for local discovery, offers, and loyalty.",
    image: require("../assets/explainer/business-list.png"),
    alt: "Cling all businesses screen with add business button",
    tone: "merchant",
  },
  {
    eyebrow: "Business profile",
    title: "Set the business category and core storefront details",
    copy:
      "The business setup flow captures the category, business name, and merchant-facing basics that make the storefront searchable and understandable.",
    narration:
      "The merchant journey starts by setting the business category and core profile details that customers will see inside Cling.",
    image: require("../assets/explainer/business-basics.png"),
    alt: "Cling business profile basics screen",
    tone: "merchant",
  },
  {
    eyebrow: "Location setup",
    title: "Add the physical location customers can discover and visit",
    copy:
      "Merchants can pin their map location and complete address details so Cling connects offline footfall to digital discovery.",
    narration:
      "Next, the merchant adds a real-world location so nearby customers can find and visit the business.",
    image: require("../assets/explainer/business-location.png"),
    alt: "Cling business location setup screen",
    tone: "merchant",
  },
  {
    eyebrow: "Preview",
    title: "Preview the storefront before publishing it live",
    copy:
      "Before launch, merchants can see how their business profile, products, rewards, and offers will be presented in the app.",
    narration:
      "Before going live, the merchant can preview exactly how the storefront will appear inside Cling.",
    image: require("../assets/explainer/business-preview.png"),
    alt: "Cling business profile preview screen",
    tone: "merchant",
  },
  {
    eyebrow: "Merchant storefront",
    title: "After setup, Cling lands the merchant on the business profile",
    copy:
      "This merchant view becomes the command center for products, offers, reward setup, QR sharing, and public profile visibility.",
    narration:
      "After setup, the merchant lands on the business profile, which becomes the main place to manage the storefront.",
    image: require("../assets/explainer/business-detail-products.png"),
    alt: "Cling merchant business page with product tab",
    tone: "merchant",
  },
  {
    eyebrow: "Products",
    title: "Add products directly from the business profile",
    copy:
      "Product creation helps merchants build a real digital storefront instead of a simple loyalty listing, with pricing, descriptions, and reuse options.",
    narration:
      "From the business profile, the merchant can begin by adding products and building out a usable storefront.",
    image: require("../assets/explainer/add-product.png"),
    alt: "Cling add product screen",
    tone: "merchant",
  },
  {
    eyebrow: "Offers",
    title: "Move to the offers tab to create live promotions",
    copy:
      "Offers live right beside products and loyalty so merchants can manage promotional activity without switching into a disconnected tool.",
    narration:
      "Once products are in place, the merchant can move into the offers tab and begin creating promotions.",
    image: require("../assets/explainer/business-offers-tab.png"),
    alt: "Cling merchant offers tab",
    tone: "merchant",
  },
  {
    eyebrow: "Offer setup",
    title: "Create a new offer with shopper-facing details and expiry",
    copy:
      "Cling supports category, name, promo code, description, and timing so the offer is immediately clear to the customer.",
    narration:
      "A merchant can create a new offer with a category, promo code, description, and validity dates that customers can understand right away.",
    image: require("../assets/explainer/add-offer.png"),
    alt: "Cling add offer screen",
    tone: "merchant",
  },
  {
    eyebrow: "Offer management",
    title: "Edit live offers as campaigns change",
    copy:
      "Offers are flexible over time, allowing merchants to refresh names, copy, linked products, and expiry as promotions evolve.",
    narration:
      "Offers are not static. Merchants can edit live promotions as campaigns change over time.",
    image: require("../assets/explainer/edit-offer.png"),
    alt: "Cling edit offer screen",
    tone: "merchant",
  },
  {
    eyebrow: "Offer experience",
    title: "Customers see a clean offer detail view with code and timing",
    copy:
      "This is where the promotion becomes actionable, with a clear code, merchant context, and validity window ready to use.",
    narration:
      "On the customer side, each offer appears in a clear detail view with the code, merchant, and timing ready to use.",
    image: require("../assets/explainer/offer-detail.png"),
    alt: "Cling offer detail screen",
    tone: "customer",
  },
  {
    eyebrow: "Loyalty launch",
    title: "Create a rewards program as the merchant’s retention engine",
    copy:
      "Loyalty is the core merchant hook: launch a rewards program quickly and create a simple reason for customers to come back again.",
    narration:
      "After products and offers, the merchant can launch a rewards program, which is the core retention engine inside Cling.",
    image: require("../assets/explainer/create-rewards.png"),
    alt: "Cling create rewards program screen",
    tone: "merchant",
  },
  {
    eyebrow: "Reward rules",
    title: "Define how points, visits, and redemptions actually work",
    copy:
      "Reward rules let merchants decide how customers earn value and what they unlock as they keep engaging with the business.",
    narration:
      "Reward rules define exactly how points or visits are earned and what customers unlock as they keep returning.",
    image: require("../assets/explainer/reward-rule.png"),
    alt: "Cling reward rule screen",
    tone: "merchant",
  },
  {
    eyebrow: "Dashboard",
    title: "Then manage loyalty from the merchant rewards dashboard",
    copy:
      "The dashboard brings together participants, redemptions, reward status, and program health in one operating view.",
    narration:
      "With the program live, the merchant can manage participants, redemptions, and program health from the rewards dashboard.",
    image: require("../assets/explainer/rewards-dashboard.png"),
    alt: "Cling rewards dashboard screen",
    tone: "merchant",
  },
  {
    eyebrow: "Customer path",
    title: "Customers can create posts with images, video, or captured moments",
    copy:
      "The customer journey is not only about rewards. Cling also supports community content creation through a simple media posting flow.",
    narration:
      "For customers, Cling is not only about rewards. They can also create posts using images, video, or live capture.",
    image: require("../assets/explainer/post-capture-menu.png"),
    alt: "Cling post capture menu",
    tone: "customer",
  },
  {
    eyebrow: "Feed",
    title: "Posted content and merchant content appear inside the feed experience",
    copy:
      "The feed helps customers stay connected to nearby businesses, live offers, and local activity in the same product environment.",
    narration:
      "That content then shows up inside the feed, where customers can stay connected to nearby businesses and ongoing activity.",
    image: require("../assets/explainer/customer-offer-post.png"),
    alt: "Cling customer feed and offer post screen",
    tone: "customer",
  },
  {
    eyebrow: "Rewards wallet",
    title: "Customers keep multiple merchant loyalty programs in one place",
    copy:
      "Instead of separate cards, paper stamps, and one-off apps, local loyalty relationships are grouped into one Cling wallet.",
    narration:
      "When it comes to loyalty, customers can keep multiple merchant programs together in one Cling wallet.",
    image: require("../assets/explainer/customer-programs.png"),
    alt: "Cling customer programs screen",
    tone: "customer",
  },
  {
    eyebrow: "Rewards summary",
    title: "Track joined programs, balances, and redemptions at a glance",
    copy:
      "Customers can quickly see what they joined, what is ready to redeem, and where they stand across their local loyalty relationships.",
    narration:
      "Customers can quickly track joined programs, points balances, and redemptions from one rewards summary view.",
    image: require("../assets/explainer/customer-rewards-summary.png"),
    alt: "Cling customer rewards summary screen",
    tone: "customer",
  },
  {
    eyebrow: "QR loop",
    title: "The standee and QR flow connect the physical store back into Cling",
    copy:
      "One scan can lead to products, offers, rewards, and reviews, closing the loop between the physical storefront and the digital experience.",
    narration:
      "Finally, the QR standee connects the physical business back into the app through products, offers, rewards, and reviews.",
    image: require("../assets/explainer/business-standee.png"),
    alt: "Cling business standee screen",
    tone: "merchant",
  },
];

const storyPoints = [
  "Signup flow: welcome, register, and land in the user’s private Cling profile.",
  "Merchant flow: add business, complete profile, then manage products, offers, and loyalty.",
  "Customer flow: post content, browse the feed, and track rewards in one wallet.",
];

const estimateSlideDuration = (slide) => {
  const narrationWordCount = (slide.narration || "").trim().split(/\s+/).filter(Boolean).length;
  const copyWordCount = (slide.copy || "").trim().split(/\s+/).filter(Boolean).length;
  const narrationMs = narrationWordCount * 440;
  const readingBufferMs = Math.max(copyWordCount * 35, 1400);

  return Math.max(5200, narrationMs + readingBufferMs);
};

const pickPreferredNarrationVoice = (voices) => {
  const indianFemaleVoice =
    voices.find(
      (voice) =>
        /^en[-_]IN$/i.test(voice.lang) &&
        /Heera|Lekha|Priya|Ananya|Aashi|Kavya|Veena|Female/i.test(voice.name)
    ) ||
    voices.find((voice) => /^en[-_]IN$/i.test(voice.lang) && /Google English India/i.test(voice.name)) ||
    voices.find((voice) => /^en[-_]IN$/i.test(voice.lang));

  if (indianFemaleVoice) {
    return indianFemaleVoice;
  }

  return voices.find((voice) =>
    /Samantha|Ava|Allison|Karen|Moira|Google US English/i.test(voice.name)
  );
};

function VideoPlayer() {
  const [activeSlide, setActiveSlide] = useState(0);
  const [isAutoplay, setIsAutoplay] = useState(true);
  const [isNarrationEnabled, setIsNarrationEnabled] = useState(true);
  const [availableVoices, setAvailableVoices] = useState([]);
  const [isVoiceCatalogReady, setIsVoiceCatalogReady] = useState(false);

  useEffect(() => {
    if (!("speechSynthesis" in window)) {
      setIsNarrationEnabled(false);
      return undefined;
    }

    const loadVoices = () => {
      const voices = window.speechSynthesis.getVoices();
      setAvailableVoices(voices);
      if (voices.length > 0) {
        setIsVoiceCatalogReady(true);
      }
    };

    loadVoices();
    window.speechSynthesis.addEventListener("voiceschanged", loadVoices);

    return () => {
      window.speechSynthesis.removeEventListener("voiceschanged", loadVoices);
      window.speechSynthesis.cancel();
    };
  }, []);

  useEffect(() => {
    if (!isNarrationEnabled || !("speechSynthesis" in window)) {
      return undefined;
    }

    if (!isVoiceCatalogReady) {
      return undefined;
    }

    window.speechSynthesis.cancel();

    const currentNarration = explainerSlides[activeSlide]?.narration;

    if (!currentNarration) {
      return undefined;
    }

    const utterance = new SpeechSynthesisUtterance(currentNarration);
    const preferredVoice = pickPreferredNarrationVoice(availableVoices);

    if (preferredVoice) {
      utterance.voice = preferredVoice;
      utterance.lang = preferredVoice.lang;
    } else {
      utterance.lang = "en-IN";
    }

    utterance.rate = 0.92;
    utterance.pitch = 0.94;
    utterance.volume = 1;

    const speakTimeout = window.setTimeout(() => {
      window.speechSynthesis.speak(utterance);
    }, 180);

    return () => {
      window.clearTimeout(speakTimeout);
      window.speechSynthesis.cancel();
    };
  }, [activeSlide, availableVoices, isNarrationEnabled, isVoiceCatalogReady]);

  useEffect(() => {
    if (!isAutoplay) {
      return undefined;
    }

    const currentSlide = explainerSlides[activeSlide];
    const autoAdvanceDelay = estimateSlideDuration(currentSlide);
    let nextSlideTimeout;

    if (
      !isNarrationEnabled ||
      !("speechSynthesis" in window) ||
      !currentSlide?.narration ||
      !isVoiceCatalogReady
    ) {
      nextSlideTimeout = window.setTimeout(() => {
        setActiveSlide((currentIndex) => (currentIndex + 1) % explainerSlides.length);
      }, autoAdvanceDelay);

      return () => window.clearTimeout(nextSlideTimeout);
    }

    const pollForSpeechCompletion = () => {
      if (window.speechSynthesis.speaking || window.speechSynthesis.pending) {
        nextSlideTimeout = window.setTimeout(pollForSpeechCompletion, 250);
        return;
      }

      nextSlideTimeout = window.setTimeout(() => {
        setActiveSlide((currentIndex) => (currentIndex + 1) % explainerSlides.length);
      }, 900);
    };

    nextSlideTimeout = window.setTimeout(pollForSpeechCompletion, 600);

    return () => window.clearTimeout(nextSlideTimeout);
  }, [activeSlide, isAutoplay, isNarrationEnabled, isVoiceCatalogReady]);

  const currentSlide = explainerSlides[activeSlide];

  return (
    <div className="cling-explainer">
      <header className="cling-explainer__header">
        <div className="cling-explainer__shell cling-explainer__header-inner">
          <Link className="cling-explainer__brand" to="/" aria-label="Cling home">
            <img src={require("../images/logo.svg").default} alt="Cling" />
          </Link>

          <nav className="cling-explainer__nav" aria-label="Explainer navigation">
            <Link to="/">Home</Link>
            <Link to="/PrivacyPolicy">Trust & Safety</Link>
            <Link to="/UserAccountDelete">Delete Account</Link>
          </nav>

          <a
            className="cling-explainer__store-link"
            href="https://play.google.com/store/apps/details?id=com.cling1.app"
            target="_blank"
            rel="noreferrer"
          >
            Get the app
          </a>
        </div>
      </header>

      <main className="cling-explainer__main">
        <section className="cling-explainer__hero">
          <div className="cling-explainer__shell cling-explainer__hero-layout">
            <div className="cling-explainer__copy">
              <p className="cling-explainer__eyebrow">Cling product explainer</p>
              <h1>See how Cling connects loyalty, storefronts, offers, and local discovery.</h1>
              <p className="cling-explainer__intro">
                This walkthrough now follows the actual product sequence using real Cling app
                screens: customer onboarding, merchant business setup, product creation, offer
                creation, loyalty management, customer wallet, and the QR standee loop that ties
                it all together.
              </p>

              <div className="cling-explainer__actions">
                <button
                  className="cling-explainer__button cling-explainer__button--primary"
                  type="button"
                  onClick={() => setIsAutoplay((currentValue) => !currentValue)}
                >
                  {isAutoplay ? "Pause explainer" : "Play explainer"}
                </button>
                <button
                  className="cling-explainer__button cling-explainer__button--secondary"
                  type="button"
                  onClick={() => setIsNarrationEnabled((currentValue) => !currentValue)}
                >
                  {isNarrationEnabled ? "Voice narration on" : "Voice narration off"}
                </button>
                <a
                  className="cling-explainer__button cling-explainer__button--secondary"
                  href="https://play.google.com/store/apps/details?id=com.cling1.app"
                  target="_blank"
                  rel="noreferrer"
                >
                  Download Cling
                </a>
              </div>

              <div className="cling-explainer__story-points">
                {storyPoints.map((point) => (
                  <div key={point} className="cling-explainer__story-point">
                    <span />
                    <p>{point}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="cling-explainer__stage">
              <div className="cling-explainer__status">
                <span>{String(activeSlide + 1).padStart(2, "0")}</span>
                <p>{currentSlide.eyebrow}</p>
              </div>

              <div className="cling-explainer__phone">
                {explainerSlides.map((slide, index) => (
                  <figure
                    key={slide.title}
                    className={`cling-explainer__frame ${
                      index === activeSlide ? "is-active" : ""
                    }`}
                  >
                    <img src={slide.image} alt={slide.alt} />
                    {slide.overlays?.map((overlay) =>
                      overlay.image ? (
                        <img
                          key={overlay.className}
                          className={overlay.className}
                          src={overlay.image}
                          alt={overlay.alt || ""}
                          aria-hidden={overlay.alt ? undefined : "true"}
                        />
                      ) : overlay.label ? (
                        <span key={overlay.className} aria-hidden="true" className={overlay.className}>
                          {overlay.label}
                        </span>
                      ) : (
                        <span
                          key={overlay.className}
                          aria-hidden="true"
                          className={overlay.className}
                        />
                      )
                    )}
                  </figure>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="cling-explainer__timeline">
          <div className="cling-explainer__shell">
            <div className="cling-explainer__timeline-panel">
              <div className="cling-explainer__timeline-copy">
                <p className="cling-explainer__eyebrow">{currentSlide.eyebrow}</p>
                <h2>{currentSlide.title}</h2>
                <p>{currentSlide.copy}</p>
              </div>

              <div className="cling-explainer__timeline-controls">
                {explainerSlides.map((slide, index) => (
                  <button
                    key={slide.title}
                    type="button"
                    className={`cling-explainer__timeline-item ${
                      index === activeSlide ? "is-active" : ""
                    }`}
                    onClick={() => setActiveSlide(index)}
                  >
                    <span className={`cling-explainer__timeline-tone cling-explainer__timeline-tone--${slide.tone}`} />
                    <strong>{String(index + 1).padStart(2, "0")}</strong>
                    <small>{slide.title}</small>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="cling-explainer__cta">
          <div className="cling-explainer__shell cling-explainer__cta-card">
            <div>
              <p className="cling-explainer__eyebrow">What this shows</p>
              <h2>Loyalty is the entry point, but the product story is much bigger.</h2>
              <p>
                Cling helps merchants run a better business through rewards, products, offers,
                reviews, and QR-driven discovery while giving customers a wallet for local loyalty
                relationships.
              </p>
            </div>

            <div className="cling-explainer__cta-actions">
              <Link className="cling-explainer__button cling-explainer__button--secondary" to="/">
                Back to homepage
              </Link>
              <a
                className="cling-explainer__button cling-explainer__button--primary"
                href="https://play.google.com/store/apps/details?id=com.cling1.app"
                target="_blank"
                rel="noreferrer"
              >
                Get Cling on Google Play
              </a>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

export default VideoPlayer;
